import { ursaColors } from "@smartsheet/design-tokens";
// TODO apply these
type IconTag = "toolbar";

export type IconSize = "16" | "20" | "24";

export interface IconInfo {
    description: string;
    sizes: Partial<Record<IconSize, string>>;
    aliases?: string[];
    // Map of ursa colors (hard-coded within the SVG files from Figma) to a prop name that will be
    // exposed to the user. If this map is specified, the generated component will contain extra
    // props, one for each entry in this map.
    colorSlots?: Partial<
        Record<
            (typeof ursaColors)[keyof typeof ursaColors],
            {
                // The prop name to add to the component.
                propName: string;
                // The tsdoc to go with it.
                tsDoc: string;
            }
        >
    >;
    // Set the default color to something other than neutral800. Must be passed as a string so it does not evaluate in the script.
    defaultColor?: string;
    tags?: IconTag[];
}

export const iconMeta: Record<string, IconInfo> = {
    ActivityIcon: {
        description: "History of activities on a sheet/report.",
        sizes: {
            "20": "./svg/activity-20px.svg",
            "24": "./svg/activity-24px.svg",
        },
        aliases: ["history"],
    },
    AlphaNumericIcon: {
        description: "",
        sizes: { "20": "./svg/alpha-numeric-20px.svg", "24": "./svg/alpha-numeric-24px.svg" },
        aliases: ["A1", "text", "number"],
        tags: [],
    },
    AnnotationArrowIcon: {
        description: "",
        sizes: { "20": "./svg/annotation-arrow-20px.svg", "24": "./svg/annotation-arrow-24px.svg" },
        aliases: ["comment", "note"],
        tags: [],
    },
    ArrowSmallDownIcon: {
        description: "Dropdown arrow",
        sizes: {
            "20": "./svg/arrow-small-down-20px.svg",
        },
        aliases: ["arrow", "down"],
    },
    AttachIcon: {
        description: "Attach files, links, etc.",
        sizes: {
            "20": "./svg/attach-20px.svg",
            "24": "./svg/attach-24px.svg",
        },
        aliases: ["file", "paperclip"],
    },
    BackIcon: {
        description: "Navigate back",
        sizes: {
            "20": "./svg/back-20px.svg",
            "24": "./svg/back-24px.svg",
        },
        aliases: ["left", "arrow"],
    },
    BackgroundColorIcon: {
        description: "Style background color of selected element.",
        sizes: {
            "20": "./svg/background-color-20px.svg",
            "24": "./svg/background-color-24px.svg",
        },
        aliases: ["fill"],
        tags: [],
    },
    BoldIcon: {
        description: "Increase font weight",
        sizes: { "20": "./svg/bold-20px.svg", "24": "./svg/bold-24px.svg" },
        aliases: [],
        tags: [],
    },
    BrandfolderIcon: {
        description: "",
        sizes: { "20": "./svg/brandfolder-20px.svg", "24": "./svg/brandfolder-24px.svg" },
        aliases: [],
        tags: [],
    },
    BullseyeIcon: {
        description: "",
        sizes: { "20": "./svg/bullseye-20px.svg", "24": "./svg/bullseye-24px.svg" },
        aliases: ["target"],
        tags: [],
    },
    CalendarIcon: {
        description: "Calendar",
        sizes: { "20": "./svg/calendar-20px.svg", "24": "./svg/calendar-24px.svg" },
        aliases: ["date", "day", "month", "year"],
        tags: [],
    },
    CalendarWithCheckmarkIcon: {
        description: "",
        sizes: { "20": "./svg/calendar-with-checkmark-20px.svg", "24": "./svg/calendar-with-checkmark-24px.svg" },
        aliases: ["date", "day", "month", "year", "due"],
        tags: [],
    },
    CalendarWithPencilIcon: {
        description: "",
        sizes: { "20": "./svg/calendar-with-pencil-20px.svg", "24": "./svg/calendar-with-pencil-24px.svg" },
        aliases: ["date", "day", "month", "year", "edit", "modify"],
        tags: [],
    },
    CardIcon: {
        description: "Card view",
        sizes: { "20": "./svg/card-20px.svg", "24": "./svg/card-24px.svg" },
        aliases: [],
        tags: [],
    },
    CardCompactIcon: {
        description: "",
        sizes: { "20": "./svg/card-compact-20px.svg", "24": "./svg/card-compact-24px.svg" },
        aliases: ["collapse"],
        tags: [],
    },
    CardCozyIcon: {
        description: "",
        sizes: { "20": "./svg/card-cozy-20px.svg", "24": "./svg/card-cozy-24px.svg" },
        aliases: ["expand", "comfortable"],
        tags: [],
    },
    CardGridIcon: {
        description: "",
        sizes: { "20": "./svg/card-grid-20px.svg", "24": "./svg/card-grid-24px.svg" },
        aliases: ["squares"],
        tags: [],
    },
    CellLinkIcon: {
        description: "",
        sizes: { "20": "./svg/cell-link-20px.svg", "24": "./svg/cell-link-24px.svg" },
        aliases: ["link"],
        tags: [],
    },
    ChecklistIcon: {
        description: "",
        sizes: { "20": "./svg/checklist-20px.svg", "24": "./svg/checklist-24px.svg" },
        aliases: ["list", "todo", "dropdown"],
        tags: [],
    },
    CheckmarkIcon: {
        description: "",
        sizes: { "20": "./svg/checkmark-20px.svg", "24": "./svg/checkmark-24px.svg" },
        aliases: ["check", "success"],
        tags: [],
    },
    CheckOnDotIcon: {
        description: "",
        sizes: {
            "20": "./svg/check-on-dot-20px.svg",
        },
        aliases: ["confirmed", "success", "approved"],
        tags: [],
        defaultColor: "ursaColors.mint400",
    },
    ChevronDownIcon: {
        description: "",
        sizes: { "20": "./svg/chevron-down-20px.svg", "24": "./svg/chevron-down-24px.svg" },
        aliases: ["caret", "chevron", "down", "expand"],
        tags: [],
    },
    ChevronLeftIcon: {
        description: "",
        sizes: { "20": "./svg/chevron-left-20px.svg", "24": "./svg/chevron-left-24px.svg" },
        aliases: ["caret", "chevron", "left"],
        tags: [],
    },
    ChevronRightIcon: {
        description: "",
        sizes: { "20": "./svg/chevron-right-20px.svg", "24": "./svg/chevron-right-24px.svg" },
        aliases: ["caret", "chevron", "collapse", "right"],
        tags: [],
    },
    ChevronUpIcon: {
        description: "",
        sizes: { "20": "./svg/chevron-up-20px.svg", "24": "./svg/chevron-up-24px.svg" },
        aliases: ["caret", "chevron", "up"],
        tags: [],
    },
    ClockIcon: {
        description: "",
        sizes: { "20": "./svg/clock-20px.svg", "24": "./svg/clock-24px.svg" },
        aliases: ["recent", "time"],
        tags: [],
    },
    CloseIcon: {
        description: "Close or dismiss an element/panel/modal/banner that is open",
        sizes: {
            "16": "./svg/close-16px.svg",
            "20": "./svg/close-20px.svg",
            "24": "./svg/close-24px.svg",
        },
        aliases: ["clear", "remove"],
        tags: [],
    },
    CloudIcon: {
        description: "",
        sizes: {
            "20": "./svg/cloud-20px.svg",
        },
        aliases: ["weather"],
    },
    CollaborateIcon: {
        description: "",
        sizes: { "20": "./svg/collaborate-20px.svg", "24": "./svg/collaborate-24px.svg" },
        aliases: ["people", "team", "users"],
        tags: [],
    },
    ColumnTypeIcon: {
        description: "",
        sizes: { "20": "./svg/column-type-20px.svg", "24": "./svg/column-type-24px.svg" },
        aliases: [],
        tags: [],
    },
    CompactIcon: {
        description: "Condensed view of elements on a page",
        sizes: { "20": "./svg/compact-20px.svg", "24": "./svg/compact-24px.svg" },
        aliases: ["dense"],
        tags: [],
    },
    ConditionalFormattingIcon: {
        description: "",
        sizes: {
            "20": "./svg/conditional-formatting-20px.svg",
            "24": "./svg/conditional-formatting-24px.svg",
        },
        aliases: ["format"],
        tags: [],
    },
    ConnectorsIcon: {
        description: "",
        sizes: { "20": "./svg/connectors-20px.svg", "24": "./svg/connectors-24px.svg" },
        aliases: ["mutual", "reciprocal"],
        tags: [],
    },
    ContentLibraryIcon: {
        description: "",
        sizes: { "20": "./svg/content-library-20px.svg", "24": "./svg/content-library-24px.svg" },
        aliases: ["folder", "images", "pictures"],
        tags: [],
    },
    ContractIcon: {
        description: "",
        sizes: { "20": "./svg/contract-20px.svg", "24": "./svg/contract-24px.svg" },
        aliases: [],
        tags: [],
    },
    ContractVerticalIcon: {
        description: "",
        sizes: { "20": "./svg/contract-vertical-20px.svg", "24": "./svg/contract-vertical-24px.svg" },
        aliases: ["collapse", "shrink", "minimize"],
        tags: [],
    },
    ConversationsIcon: {
        description: "",
        sizes: {
            "20": "./svg/conversations-20px.svg",
            "24": "./svg/conversations-24px.svg",
        },
        aliases: ["comments"],
        tags: [],
    },
    CozyIcon: {
        description: "More spacious view of elements on a page",
        sizes: { "20": "./svg/cozy-20px.svg", "24": "./svg/cozy-24px.svg" },
        aliases: ["airy", "comfortable"],
        tags: [],
    },
    CreditCardIcon: {
        description: "",
        sizes: { "20": "./svg/credit-card-20px.svg", "24": "./svg/credit-card-24px.svg" },
        aliases: ["payment"],
        tags: [],
    },
    CurrencyBrazilRealIcon: {
        description: "",
        sizes: {
            "24": "./svg/currency-brazil-real-24px.svg",
        },
        aliases: ["BRL"],
        tags: [],
    },
    CurrencyDollarIcon: {
        description: "",
        sizes: {
            "24": "./svg/currency-dollar-24px.svg",
        },
        aliases: ["USD", "AUD", "CAD", "NZD", "SGD", "HKD"],
        tags: [],
    },
    CurrencyEuroIcon: {
        description: "",
        sizes: {
            "24": "./svg/currency-euro-24px.svg",
        },
        aliases: ["EUR"],
        tags: [],
    },
    CurrencyIndianRupeeIcon: {
        description: "",
        sizes: {
            "24": "./svg/currency-indian-rupee-24px.svg",
        },
        aliases: ["INR"],
        tags: [],
    },
    CurrencyIsraelShekelIcon: {
        description: "",
        sizes: {
            "24": "./svg/currency-israel-shekel-24px.svg",
        },
        aliases: ["ILS"],
        tags: [],
    },
    CurrencyJapaneseYenIcon: {
        description: "",
        sizes: {
            "24": "./svg/currency-japanese-yen-24px.svg",
        },
        aliases: ["JPY"],
        tags: [],
    },
    CurrencyDanishKroneIcon: {
        description: "",
        sizes: {
            "24": "./svg/currency-danish-krone-24px.svg",
        },
        aliases: ["DKK"],
        tags: [],
    },
    CurrencyNorwegianKroneIcon: {
        description: "",
        sizes: {
            "24": "./svg/currency-norwegian-krone-24px.svg",
        },
        aliases: ["NOK"],
        tags: [],
    },
    CurrencySwedishKronaIcon: {
        description: "",
        sizes: {
            "24": "./svg/currency-swedish-krona-24px.svg",
        },
        aliases: ["SEK"],
        tags: [],
    },
    CurrencyRussianRubleIcon: {
        description: "",
        sizes: {
            "24": "./svg/currency-russian-ruble-24px.svg",
        },
        aliases: ["RUB"],
        tags: [],
    },
    CurrencySouthAfricanRandIcon: {
        description: "",
        sizes: {
            "24": "./svg/currency-south-african-rand-24px.svg",
        },
        aliases: ["ZAR"],
        tags: [],
    },
    CurrencySouthKoreanWonIcon: {
        description: "",
        sizes: {
            "24": "./svg/currency-south-korean-won-24px.svg",
        },
        aliases: ["KRW"],
        tags: [],
    },
    CurrencySwissFrancIcon: {
        description: "",
        sizes: {
            "24": "./svg/currency-swiss-franc-24px.svg",
        },
        aliases: ["CHF"],
        tags: [],
    },
    CurrencyBritishPoundIcon: {
        description: "",
        sizes: {
            "24": "./svg/currency-uk-pound-24px.svg",
        },
        aliases: ["GBP"],
        tags: [],
    },
    DashIcon: {
        description: "",
        sizes: {
            "20": "./svg/dash-20px.svg",
        },
        aliases: ["line", "bar", "emdash"],
        tags: [],
        defaultColor: "ursaColors.gold500",
    },
    DataMeshIcon: {
        description: "",
        sizes: { "20": "./svg/data-mesh-20px.svg", "24": "./svg/data-mesh-24px.svg" },
        aliases: [],
        tags: [],
    },
    DatatableIcon: {
        description: "",
        sizes: { "20": "./svg/datatable-20px.svg", "24": "./svg/datatable-24px.svg" },
        aliases: [],
        tags: [],
    },
    DateIcon: {
        description: "",
        sizes: { "20": "./svg/date-20px.svg", "24": "./svg/date-24px.svg" },
        aliases: ["calendar", "day", "month", "year"],
        tags: [],
    },
    DecreaseDecimalIcon: {
        description: "",
        sizes: {
            "20": "./svg/decrease-decimal-20px.svg",
            "24": "./svg/decrease-decimal-24px.svg",
        },
        aliases: [],
        tags: [],
    },
    DiagonalArrowIcon: {
        description: "",
        sizes: {
            "20": "./svg/diagonal-arrow-20px.svg",
        },
        aliases: ["point", "point up", "point down"],
    },
    DiamondIcon: {
        description: "",
        sizes: { "20": "./svg/diamond-20px.svg" },
        aliases: [""],
        tags: [],
        defaultColor: "ursaColors.neutral500",
    },
    DividerIcon: {
        description: "",
        sizes: { "20": "./svg/divider-20px.svg", "24": "./svg/divider-24px.svg" },
        aliases: ["line"],
        tags: [],
    },
    DollarsIcon: {
        description: "",
        sizes: { "20": "./svg/dollars-20px.svg", "24": "./svg/dollars-24px.svg" },
        aliases: ["money"],
        tags: [],
    },
    DotIcon: {
        description: "",
        sizes: { "20": "./svg/dot-20px.svg" },
        aliases: ["circle"],
        tags: [],
    },
    DoubleChevronLeftIcon: {
        description: "",
        sizes: {
            "20": "./svg/double-chevron-left-20px.svg",
            "24": "./svg/double-chevron-left-24px.svg",
        },
        aliases: ["chevron", "left"],
        tags: [],
    },
    DoubleChevronRightIcon: {
        description: "",
        sizes: {
            "20": "./svg/double-chevron-right-20px.svg",
            "24": "./svg/double-chevron-right-24px.svg",
        },
        aliases: ["chevron", "right"],
        tags: [],
    },
    DoubleDiamondIcon: {
        description: "",
        sizes: { "20": "./svg/double-diamond-20px.svg" },
        aliases: ["black diamond"],
        tags: [],
        defaultColor: "ursaColors.neutral500",
    },
    DownloadIcon: {
        description: "",
        sizes: { "20": "./svg/download-20px.svg", "24": "./svg/download-24px.svg" },
        aliases: ["export", "save"],
        tags: [],
    },
    DynamicViewIcon: {
        description: "",
        sizes: { "20": "./svg/dynamic-view-20px.svg", "24": "./svg/dynamic-view-24px.svg" },
        aliases: ["filter", "grid"],
        tags: [],
    },
    EraserIcon: {
        description: "",
        sizes: { "20": "./svg/eraser-20px.svg", "24": "./svg/eraser-24px.svg" },
        aliases: ["delete", "remove"],
        tags: [],
    },
    ErrorIcon: {
        description: "",
        sizes: {
            "16": "./svg/error-16px.svg",
            "20": "./svg/error-20px.svg",
            "24": "./svg/error-24px.svg",
        },
        aliases: ["exclamation mark"],
        tags: [],
    },
    ExclamationOnDotIcon: {
        description: "",
        sizes: {
            "20": "./svg/exclamation-on-dot-20px.svg",
        },
        aliases: ["alert", "warning", "on hold"],
        tags: [],
        defaultColor: "ursaColors.gold500",
    },
    ExpandIcon: {
        description: "",
        sizes: { "20": "./svg/expand-20px.svg", "24": "./svg/expand-24px.svg" },
        aliases: ["maximize"],
        tags: [],
    },
    ExpandLeftIcon: {
        description: "",
        sizes: { "20": "./svg/expand-left-20px.svg", "24": "./svg/expand-left-24px.svg" },
        aliases: ["collapse", "expand", "left"],
        tags: [],
    },
    ExpandRightIcon: {
        description: "",
        sizes: { "20": "./svg/expand-right-20px.svg", "24": "./svg/expand-right-24px.svg" },
        aliases: ["collapse", "expand", "right"],
        tags: [],
    },
    ExpandVerticalIcon: {
        description: "",
        sizes: { "20": "./svg/expand-vertical-20px.svg", "24": "./svg/expand-vertical-24px.svg" },
        aliases: ["expand", "full screen", "maximize"],
        tags: [],
    },
    EyeHideIcon: {
        description: "",
        sizes: { "20": "./svg/eye-hide-20px.svg", "24": "./svg/eye-hide-24px.svg" },
        aliases: ["hide", "invisible", "mask", "password"],
        tags: [],
    },
    EyeShowIcon: {
        description: "",
        sizes: { "20": "./svg/eye-show-20px.svg", "24": "./svg/eye-show-24px.svg" },
        aliases: ["show", "visible", "view"],
        tags: [],
    },
    FastForwardIcon: {
        description: "",
        sizes: { "20": "./svg/fast-forward-20px.svg" },
        aliases: [],
        tags: [],
        defaultColor: "ursaColors.neutral500",
    },
    FeedbackIcon: {
        description: "",
        sizes: { "20": "./svg/feedback-20px.svg", "24": "./svg/feedback-24px.svg" },
        aliases: [],
        tags: [],
    },
    FilterIcon: {
        description: "",
        sizes: { "20": "./svg/filter-20px.svg", "24": "./svg/filter-24px.svg" },
        aliases: ["funnel"],
        tags: [],
    },
    FilterOffIcon: {
        description: "",
        sizes: { "20": "./svg/filter-off-20px.svg", "24": "./svg/filter-off-24px.svg" },
        aliases: ["funnel"],
        tags: [],
    },
    FlagIcon: {
        description: "",
        sizes: { "20": "./svg/flag-20px.svg", "24": "./svg/flag-24px.svg" },
        aliases: [],
        tags: [],
    },
    FlagFilledIcon: {
        description: "",
        sizes: { "20": "./svg/flag-filled-20px.svg", "24": "./svg/flag-filled-24px.svg" },
        aliases: [],
        tags: [],
    },
    FolderIcon: {
        description: "",
        sizes: {
            "20": "./svg/folder-20px.svg",
            "24": "./svg/folder-24px.svg",
        },
        aliases: [],
        tags: [],
    },
    FormatIcon: {
        description: "Format Icon for New Views Toolbar",
        sizes: {
            "20": "./svg/format-20px.svg",
            "24": "./svg/format-24px.svg",
        },
        aliases: ["edit", "style"],
        tags: [],
    },
    FormsIcon: {
        description: "Forms",
        sizes: { "20": "./svg/forms-20px.svg", "24": "./svg/forms-24px.svg" },
        aliases: [],
        tags: [],
    },
    FormulaIcon: {
        description: "",
        sizes: {
            "16": "./svg/formula-16px.svg",
            "20": "./svg/formula-20px.svg",
            "24": "./svg/formula-24px.svg",
        },
        aliases: ["function"],
        tags: [],
    },
    FunctionIcon: {
        description: "",
        sizes: { "20": "./svg/function-20px.svg", "24": "./svg/function-24px.svg" },
        aliases: ["formula"],
        tags: [],
    },
    GanttIcon: {
        description: "",
        sizes: { "20": "./svg/gantt-20px.svg", "24": "./svg/gantt-24px.svg" },
        aliases: [],
        tags: [],
    },
    GoToDateIcon: {
        description: "",
        sizes: { "20": "./svg/go-to-date-20px.svg", "24": "./svg/go-to-date-24px.svg" },
        aliases: [],
        tags: [],
    },
    GridIcon: {
        description: "Grid view",
        sizes: { "20": "./svg/grid-20px.svg", "24": "./svg/grid-24px.svg" },
        aliases: [],
        tags: [],
    },
    GroupIcon: {
        description: "",
        sizes: { "20": "./svg/group-20px.svg", "24": "./svg/group-24px.svg" },
        aliases: ["combine"],
        tags: [],
    },
    HashmarkIcon: {
        description: "",
        sizes: { "20": "./svg/hashmark-20px.svg", "24": "./svg/hashmark-24px.svg" },
        aliases: ["hashtag", "number", "pound"],
        tags: [],
    },
    HeaderIcon: {
        description: "",
        sizes: { "20": "./svg/header-20px.svg", "24": "./svg/header-24px.svg" },
        aliases: ["title"],
        tags: [],
    },
    HeartFilledIcon: {
        description: "",
        sizes: { "20": "./svg/heart-filled-20px.svg" },
        aliases: ["love"],
        tags: [],
        defaultColor: "ursaColors.red400",
    },
    HeartIcon: {
        description: "",
        sizes: { "20": "./svg/heart-20px.svg" },
        aliases: ["love"],
        tags: [],
        defaultColor: "ursaColors.neutral300",
    },
    HelpIcon: {
        description: "",
        sizes: { "20": "./svg/help-20px.svg", "24": "./svg/help-24px.svg" },
        aliases: ["question mark", "support"],
        tags: [],
    },
    HighlightChangesIcon: {
        description: "",
        sizes: {
            "20": "./svg/highlight-changes-20px.svg",
            "24": "./svg/highlight-changes-24px.svg",
        },
        aliases: [],
        tags: [],
        colorSlots: {
            [ursaColors.blue500]: {
                tsDoc: "The color of the highlight bar on the bottom.",
                propName: "selectedColor",
            },
        },
    },
    ImageIcon: {
        description: "",
        sizes: { "20": "./svg/image-20px.svg", "24": "./svg/image-24px.svg" },
        aliases: ["picture"],
        tags: [],
    },
    IncreaseDecimalIcon: {
        description: "",
        sizes: {
            "20": "./svg/increase-decimal-20px.svg",
            "24": "./svg/increase-decimal-24px.svg",
        },
        aliases: [],
        tags: [],
    },
    IndentIcon: {
        description: "",
        sizes: { "20": "./svg/indent-20px.svg", "24": "./svg/indent-24px.svg" },
        aliases: ["tab"],
        tags: [],
    },
    InfoIcon: {
        description: "",
        sizes: { "16": "./svg/info-16px.svg", "20": "./svg/info-20px.svg", "24": "./svg/info-24px.svg" },
        aliases: [],
        tags: [],
    },
    InsertColumnLeftIcon: {
        description: "",
        sizes: { "20": "./svg/insert-column-left-20px.svg", "24": "./svg/insert-column-left-24px.svg" },
        aliases: ["add"],
        tags: [],
    },
    InsertColumnRightIcon: {
        description: "",
        sizes: { "20": "./svg/insert-column-right-20px.svg", "24": "./svg/insert-column-right-24px.svg" },
        aliases: ["add"],
        tags: [],
    },
    InsertRowAboveIcon: {
        description: "",
        sizes: { "20": "./svg/insert-row-above-20px.svg", "24": "./svg/insert-row-above-24px.svg" },
        aliases: ["add"],
        tags: [],
    },
    InsertRowBelowIcon: {
        description: "",
        sizes: { "20": "./svg/insert-row-below-20px.svg", "24": "./svg/insert-row-below-24px.svg" },
        aliases: ["add"],
        tags: [],
    },
    InsightsIcon: {
        description: "",
        sizes: { "20": "./svg/insights-20px.svg", "24": "./svg/insights-24px.svg" },
        aliases: ["charts", "metrics"],
        tags: [],
    },
    ItalicIcon: {
        description: "",
        sizes: { "20": "./svg/italic-20px.svg", "24": "./svg/italic-24px.svg" },
        aliases: ["emphasis"],
        tags: [],
    },
    LevelsIcon: {
        description: "",
        sizes: { "20": "./svg/levels-20px.svg", "24": "./svg/levels-24px.svg" },
        aliases: ["hierarchy"],
        tags: [],
    },
    LinkIcon: {
        description: "",
        sizes: { "20": "./svg/link-20px.svg", "24": "./svg/link-24px.svg" },
        aliases: ["hyperlink"],
        tags: [],
    },
    LockIcon: { description: "", sizes: { "16": "./svg/lock-16px.svg" }, aliases: [], tags: [] },
    MagnifyingGlassIcon: {
        description: "",
        sizes: {
            "20": "./svg/magnifying-glass-20px.svg",
            "24": "./svg/magnifying-glass-24px.svg",
        },
        aliases: ["search"],
        tags: [],
    },
    MegaphoneIcon: {
        description: "",
        sizes: {
            "20": "./svg/megaphone-20px.svg",
            "24": "./svg/megaphone-24px.svg",
        },
        aliases: [],
        tags: [],
    },
    MessageIcon: {
        description: "",
        sizes: { "20": "./svg/message-20px.svg", "24": "./svg/message-24px.svg" },
        aliases: ["chat", "comment"],
        tags: [],
    },
    MildPainIcon: {
        description: "",
        sizes: { "20": "./svg/mild-pain-20px.svg" },
        aliases: ["emoji", "face", "smile"],
        defaultColor: "ursaColors.green100",
    },
    MinusIcon: {
        description: "",
        sizes: { "20": "./svg/minus-20px.svg", "24": "./svg/minus-24px.svg" },
        aliases: ["remove", "subtract"],
        tags: [],
    },
    ModeratePainIcon: {
        description: "",
        sizes: {
            "20": "./svg/moderate-pain-20px.svg",
        },
        aliases: ["face", "emoji", "apathy"],
        defaultColor: "ursaColors.yellow300",
    },
    MoreIcon: {
        description: "",
        sizes: { "20": "./svg/more-20px.svg", "24": "./svg/more-24px.svg" },
        aliases: ["kebab", "overflow"],
        tags: [],
    },
    NewWindowIcon: {
        description: "",
        sizes: { "20": "./svg/new-window-20px.svg", "24": "./svg/new-window-24px.svg" },
        aliases: ["tab"],
        tags: [],
    },
    NoPainIcon: {
        description: "",
        sizes: {
            "20": "./svg/no-pain-20px.svg",
        },
        aliases: ["face", "emoji", "happy", "smile"],
        defaultColor: "ursaColors.mint400",
    },
    OctagonIcon: {
        description: "",
        sizes: {
            "20": "./svg/octagon-20px.svg",
        },
        aliases: [],
        defaultColor: "ursaColors.red400",
    },
    OpenBookIcon: {
        description: "",
        sizes: { "20": "./svg/open-book-20px.svg", "24": "./svg/open-book-24px.svg" },
        aliases: ["manual", "read"],
        tags: [],
    },
    OutdentIcon: {
        description: "",
        sizes: { "20": "./svg/outdent-20px.svg", "24": "./svg/outdent-24px.svg" },
        aliases: ["tab"],
        tags: [],
    },
    PaintRollerIcon: {
        description: "",
        sizes: { "20": "./svg/paintroller-20px.svg", "24": "./svg/paintroller-24px.svg" },
        aliases: ["format"],
        tags: [],
    },
    PartlySunnyIcon: {
        description: "",
        sizes: {
            "20": "./svg/partly-sunny-20px.svg",
        },
        aliases: ["sunshine", "weather", "cloud"],
    },
    PauseIcon: {
        description: "",
        sizes: { "20": "./svg/pause-20px.svg" },
        aliases: ["parallel lines"],
        tags: [],
        defaultColor: "ursaColors.neutral500",
    },
    PencilIcon: {
        description: "",
        sizes: { "20": "./svg/pencil-20px.svg", "24": "./svg/pencil-24px.svg" },
        aliases: ["edit", "write"],
        tags: [],
    },
    PercentageIcon: {
        description: "",
        sizes: { "20": "./svg/percentage-20px.svg", "24": "./svg/percentage-24px.svg" },
        aliases: [],
        tags: [],
    },
    PlusIcon: {
        description: "",
        sizes: { "20": "./svg/plus-20px.svg", "24": "./svg/plus-24px.svg" },
        aliases: ["add", "insert"],
        tags: [],
    },
    ProofBoxDashedIcon: {
        description: "",
        sizes: { "20": "./svg/proof-box-dashed-20px.svg", "24": "./svg/proof-box-dashed-24px.svg" },
        aliases: ["iterate", "approve", "orbit"],
        tags: [],
    },
    ProofBoxSolidIcon: {
        description: "",
        sizes: { "20": "./svg/proof-box-solid-20px.svg", "24": "./svg/proof-box-solid-24px.svg" },
        aliases: ["iterate", "approve", "orbit"],
        tags: [],
    },
    ProofCircleDashedIcon: {
        description: "",
        sizes: { "20": "./svg/proof-circle-dashed-20px.svg", "24": "./svg/proof-circle-dashed-24px.svg" },
        aliases: ["iterate", "approve", "orbit"],
        tags: [],
    },
    ProofCircleSolidIcon: {
        description: "",
        sizes: { "20": "./svg/proof-circle-solid-20px.svg", "24": "./svg/proof-circle-solid-24px.svg" },
        aliases: ["iterate", "approve", "orbit"],
        tags: [],
    },
    RainIcon: {
        description: "",
        sizes: {
            "20": "./svg/rain-20px.svg",
        },
        aliases: ["cloud", "weather"],
    },
    RedoIcon: {
        description: "",
        sizes: { "20": "./svg/redo-20px.svg", "24": "./svg/redo-24px.svg" },
        aliases: [],
        tags: [],
    },
    ReplyIcon: {
        description: "",
        sizes: { "20": "./svg/reply-20px.svg", "24": "./svg/reply-24px.svg" },
        aliases: ["thread", "comment"],
        tags: [],
    },
    RequestsIcon: {
        description: "",
        sizes: { "20": "./svg/requests-20px.svg", "24": "./svg/requests-24px.svg" },
        aliases: ["approve", "update"],
        tags: [],
    },
    ResourceManagementIcon: {
        description: "",
        sizes: {
            "20": "./svg/resource-management-20px.svg",
            "24": "./svg/resource-management-24px.svg",
        },
        aliases: [],
        tags: [],
    },
    RewindIcon: {
        description: "",
        sizes: { "20": "./svg/rewind-20px.svg" },
        aliases: [],
        tags: [],
        defaultColor: "ursaColors.neutral500",
    },
    SettingsIcon: {
        description: "",
        sizes: { "20": "./svg/settings-20px.svg", "24": "./svg/settings-24px.svg" },
        aliases: ["gear", "preferences"],
        tags: [],
    },
    SeverePainIcon: {
        description: "",
        sizes: {
            "20": "./svg/severe-pain-20px.svg",
        },
        aliases: ["face", "emoji", "unhappy"],
        defaultColor: "ursaColors.gold500",
    },
    SheetIcon: {
        description: "Sheet",
        sizes: { "20": "./svg/sheet-20px.svg", "24": "./svg/sheet-24px.svg" },
        aliases: ["asset", "file"],
        tags: [],
    },
    SheetSummaryIcon: {
        description: "",
        sizes: { "20": "./svg/sheet-summary-20px.svg", "24": "./svg/sheet-summary-24px.svg" },
        aliases: [],
        tags: [],
    },
    SmileyFaceIcon: {
        description: "",
        sizes: { "20": "./svg/smiley-face-20px.svg", "24": "./svg/smiley-face-24px.svg" },
        aliases: ["emoji", "face", "happy"],
        tags: [],
    },
    SortIcon: {
        description: "Rearranging results",
        sizes: { "20": "./svg/sort-20px.svg", "24": "./svg/sort-24px.svg" },
        aliases: [],
        tags: [],
    },
    SquareIcon: {
        description: "",
        sizes: { "20": "./svg/square-20px.svg" },
        aliases: ["stop", "box"],
        tags: [],
        defaultColor: "ursaColors.neutral500",
    },
    StarFilledIcon: {
        description: "",
        sizes: { "20": "./svg/star-filled-20px.svg", "24": "./svg/star-filled-24px.svg" },
        aliases: ["favorite"],
        tags: [],
    },
    StarOutlineIcon: {
        description: "",
        sizes: { "20": "./svg/star-outline-20px.svg", "24": "./svg/star-outline-24px.svg" },
        aliases: ["favorite"],
        tags: [],
    },
    StatusIcon: {
        description: "",
        sizes: { "20": "./svg/status-20px.svg", "24": "./svg/status-24px.svg" },
        aliases: ["save"],
        tags: [],
    },
    StatusIssueServiceDownIcon: {
        description: "",
        sizes: {
            "20": "./svg/status-issue-service-down-20px.svg",
            "24": "./svg/status-issue-service-down-24px.svg",
        },
        aliases: ["save"],
        tags: [],
    },
    StatusUpdatedIcon: {
        description: "",
        sizes: {
            "20": "./svg/status-updated-20px.svg",
            "24": "./svg/status-updated-24px.svg",
        },
        aliases: ["save"],
        tags: [],
    },
    StatusUpdatingIcon: {
        description: "",
        sizes: {
            "20": "./svg/status-updating-20px.svg",
            "24": "./svg/status-updating-24px.svg",
        },
        aliases: ["save"],
        tags: [],
    },
    StormIcon: {
        description: "",
        sizes: {
            "20": "./svg/storm-20px.svg",
        },
        aliases: ["cloud", "weather", "lightning"],
    },
    StrikeoutIcon: {
        description: "",
        sizes: { "20": "./svg/strikeout-20px.svg", "24": "./svg/strikeout-24px.svg" },
        aliases: ["strikethrough"],
        tags: [],
    },
    StyleIcon: {
        description: "",
        sizes: { "20": "./svg/style-20px.svg", "24": "./svg/style-24px.svg" },
        aliases: ["paint bucket"],
        tags: [],
    },
    SubtaskIcon: {
        description: "",
        sizes: { "20": "./svg/subtask-20px.svg", "24": "./svg/subtask-24px.svg" },
        aliases: [],
        tags: [],
    },
    SunnyIcon: {
        description: "",
        sizes: {
            "20": "./svg/sunny-20px.svg",
        },
        aliases: ["sunshine", "weather"],
    },
    SymbolsIcon: {
        description: "",
        sizes: { "20": "./svg/symbols-20px.svg", "24": "./svg/symbols-24px.svg" },
        aliases: ["shapes", "circle", "square"],
        tags: [],
    },
    TagIcon: {
        description: "",
        sizes: { "20": "./svg/tag-20px.svg", "24": "./svg/tag-24px.svg" },
        aliases: ["label"],
        tags: [],
    },
    TemplateIcon: {
        description: "",
        sizes: { "20": "./svg/template-20px.svg", "24": "./svg/template-24px.svg" },
        aliases: [],
        tags: [],
    },
    TextIcon: {
        description: "",
        sizes: { "20": "./svg/text-20px.svg", "24": "./svg/text-24px.svg" },
        aliases: ["A"],
        tags: [],
    },
    TextAlignmentIcon: {
        description: "",
        sizes: {
            "20": "./svg/text-alignment-20px.svg",
            "24": "./svg/text-alignment-24px.svg",
        },
        aliases: ["left"],
        tags: [],
    },
    TextAlignmentBottomIcon: {
        description: "",
        sizes: {
            "24": "./svg/text-alignment-bottom-24px.svg",
        },
        aliases: [],
        tags: [],
    },

    TextAlignmentCenterIcon: {
        description: "",
        sizes: {
            "24": "./svg/text-alignment-center-24px.svg",
        },
        aliases: [],
        tags: [],
    },

    TextAlignmentMiddleIcon: {
        description: "",
        sizes: {
            "24": "./svg/text-alignment-middle-24px.svg",
        },
        aliases: [],
        tags: [],
    },

    TextAlignmentRightIcon: {
        description: "",
        sizes: {
            "24": "./svg/text-alignment-right-24px.svg",
        },
        aliases: [],
        tags: [],
    },
    TextAlignmentTopIcon: {
        description: "",
        sizes: {
            "24": "./svg/text-alignment-top-24px.svg",
        },
        aliases: [],
        tags: [],
    },

    TextColorIcon: {
        description: "",
        sizes: { "20": "./svg/text-color-20px.svg", "24": "./svg/text-color-24px.svg" },
        aliases: ["color", "text"],
        tags: [],
    },
    TextWrapIcon: {
        description: "",
        sizes: { "20": "./svg/text-wrap-20px.svg", "24": "./svg/text-wrap-24px.svg" },
        aliases: ["wrap"],
        tags: [],
    },
    ThousandsIcon: {
        description: "",
        sizes: { "20": "./svg/thousands-20px.svg", "24": "./svg/thousands-24px.svg" },
        aliases: [],
        tags: [],
    },
    TimelineIcon: {
        description: "",
        sizes: { "20": "./svg/timeline-20px.svg", "24": "./svg/timeline-24px.svg" },
        aliases: ["roadmap"],
        tags: [],
    },
    TimescaleIcon: {
        description: "",
        sizes: { "20": "./svg/timescale-20px.svg", "24": "./svg/timescale-24px.svg" },
        aliases: [],
        tags: [],
    },
    TrashcanIcon: {
        description: "",
        sizes: { "20": "./svg/trashcan-20px.svg", "24": "./svg/trashcan-24px.svg" },
        aliases: ["delete", "remove"],
        tags: [],
    },
    TriangleIcon: {
        description: "Used in the green-up and red-down custom icon, as well as the Play icon.",
        sizes: {
            "20": "./svg/triangle-20px.svg",
        },
        aliases: ["up", "down", "play"],
        tags: [],
    },
    TriangleRoundedIcon: {
        description: "",
        sizes: {
            "20": "./svg/triangle-rounded-20px.svg",
        },
        aliases: [],
        defaultColor: "ursaColors.gold500",
    },
    TriggerIcon: {
        description: "",
        sizes: { "20": "./svg/trigger-20px.svg", "24": "./svg/trigger-24px.svg" },
        aliases: ["lightning", "bolt"],
        tags: [],
    },
    UnderlineIcon: {
        description: "",
        sizes: { "20": "./svg/underline-20px.svg", "24": "./svg/underline-24px.svg" },
        aliases: [],
        tags: [],
    },
    UndoIcon: {
        description: "",
        sizes: { "20": "./svg/undo-20px.svg", "24": "./svg/undo-24px.svg" },
        aliases: [],
        tags: [],
    },
    UntagIcon: {
        description: "",
        sizes: { "20": "./svg/untag-20px.svg", "24": "./svg/untag-24px.svg" },
        aliases: ["remove", "label"],
        tags: [],
    },
    UploadIcon: {
        description: "",
        sizes: { "20": "./svg/upload-20px.svg", "24": "./svg/upload-24px.svg" },
        aliases: ["import"],
        tags: [],
    },
    UploadDatatableIcon: {
        description: "",
        sizes: { "20": "./svg/upload-datatable-20px.svg", "24": "./svg/upload-datatable-24px.svg" },
        aliases: [],
        tags: [],
    },
    UserIcon: {
        description: "",
        sizes: { "20": "./svg/user-20px.svg", "24": "./svg/user-24px.svg" },
        aliases: ["avatar", "person", "human"],
        tags: [],
    },
    UserWithCheckmarkIcon: {
        description: "",
        sizes: { "20": "./svg/user-with-checkmark-20px.svg", "24": "./svg/user-with-checkmark-24px.svg" },
        aliases: ["avatar", "person", "human"],
        tags: [],
    },
    UserWithPencil: {
        description: "",
        sizes: { "20": "./svg/user-with-pencil-20px.svg", "24": "./svg/user-with-pencil-24px.svg" },
        aliases: ["edit", "profile"],
        tags: [],
    },
    UserWithPlusIcon: {
        description: "",
        sizes: { "20": "./svg/user-with-plus-20px.svg", "24": "./svg/user-with-plus-24px.svg" },
        aliases: ["person", "profile", "team"],
        tags: [],
    },
    VersionCurrentIcon: {
        description: "",
        sizes: { "20": "./svg/version-current-20px.svg", "24": "./svg/version-current-24px.svg" },
        aliases: ["stack"],
        tags: [],
    },
    VersionCompareIcon: {
        description: "",
        sizes: { "20": "./svg/version-compare-20px.svg", "24": "./svg/version-compare-24px.svg" },
        aliases: ["stack"],
        tags: [],
    },
    VersionPreviousIcon: {
        description: "",
        sizes: { "20": "./svg/version-previous-20px.svg", "24": "./svg/version-previous-24px.svg" },
        aliases: ["stack"],
        tags: [],
    },
    VerySeverePainIcon: {
        description: "",
        sizes: {
            "20": "./svg/very-severe-pain-20px.svg",
        },
        aliases: ["face", "emoji", "unhappy"],
        defaultColor: "ursaColors.orange400",
    },
    WorkflowAutomationsIcon: {
        description: "",
        sizes: {
            "20": "./svg/workflow-automations-20px.svg",
            "24": "./svg/workflow-automations-24px.svg",
        },
        aliases: [],
        tags: [],
    },
    WorkspacesIcon: {
        description: "",
        sizes: { "20": "./svg/workspaces-20px.svg", "24": "./svg/workspaces-24px.svg" },
        aliases: [],
        tags: [],
    },
    WorstPainPossibleIcon: {
        description: "",
        sizes: {
            "20": "./svg/worst-pain-possible-20px.svg",
        },
        aliases: ["face", "emoji", "unhappy", "angry"],
        defaultColor: "ursaColors.red400",
    },
    XOnDotIcon: {
        description: "",
        sizes: {
            "20": "./svg/x-on-dot-20px.svg",
        },
        aliases: ["forbidden", "no", "error"],
        tags: [],
        defaultColor: "ursaColors.red400",
    },
    ZoomInIcon: {
        description: "",
        sizes: { "20": "./svg/zoom-in-20px.svg", "24": "./svg/zoom-in-24px.svg" },
        aliases: ["add", "plus"],
        tags: [],
    },
    ZoomInMagnifyingGlassIcon: {
        description: "",
        sizes: { "20": "./svg/zoom-in-magnifying-glass-20px.svg", "24": "./svg/zoom-in-magnifying-glass-24px.svg" },
        aliases: ["zoom"],
        tags: [],
    },
    ZoomOutIcon: {
        description: "",
        sizes: { "20": "./svg/zoom-out-20px.svg", "24": "./svg/zoom-out-24px.svg" },
        aliases: ["remove", "subtract"],
        tags: [],
    },
    ZoomOutMagnifyingGlassIcon: {
        description: "",
        sizes: { "20": "./svg/zoom-out-magnifying-glass-20px.svg", "24": "./svg/zoom-out-magnifying-glass-24px.svg" },
        aliases: ["zoom"],
        tags: [],
    },
};
